import toast from 'react-hot-toast'
import { useAppDispatch } from '../../reducers/store'
import { IntegrationAction, IntegrationDetails } from '../../types/IntegrationTypes'
import { deleteScim } from '../../reducers/settings'
import { fetchIntegrationsList } from '../../reducers/integrations'
import IntegrationRemoveModal from './IntegrationRemoveModal'
import { useState } from 'react'
import { Tooltip } from '@radix-ui/themes'
import { BOTTOM_TOAST_CONFIG } from '../toast/consts'
import { useSelector } from 'react-redux'
import { selectUser } from '../../reducers/users'

interface IIntegrationCardProps {
  integration: IntegrationDetails
  manageIntegration: (integration: IntegrationDetails) => void
  tooltip?: string
  disabled?: boolean
}

function IntegrationCard(props: IIntegrationCardProps) {
  const di = useAppDispatch()
  const [showDisconnectModal, setShowDisconnectModal] = useState(false)
  const { integration, manageIntegration, tooltip, disabled } = props
  const { darkModeEnabled } = useSelector(selectUser)

  const executeAction = async (action: IntegrationAction | undefined) => {
    if (action?.type === 'disconnect_url' && action.urlType === 'iverify' && action.url) {
      await di(deleteScim()).then(async () => {
        await di(fetchIntegrationsList())
        toast.success('SCIM disconnected', BOTTOM_TOAST_CONFIG)
      })
    }
  }

  return (
    <>
      <div className="w-full p-4 bg-cardFill border border-mainBorder rounded-lg flex flex-col justify-between gap-3">
        <div className="block">
          <div className="flex items-center">
            <img
             src={`${integration.image == '/okta.png' && darkModeEnabled ? '/okta-dark.png' : integration.image}`}
              alt="integration logo"
              className="w-10 h-10"
              onError={(e) => {
                const target = e.currentTarget as HTMLImageElement
                if (target.src !== window.location.origin + '/default_logo.png') {
                  target.src = '/default_logo.png'
                }
              }}
            />
            <div className="ml-4">
              <div className=" flex gap-2 text-xs text-neutral-11 dark:text-neutral-8 font-medium ">
                <div className="uppercase rounded-sm m-0 bg-neutral-3 dark:bg-tableHeaderSecondary w-fit px-1">
                  {integration.category}
                </div>
                {integration.isActive && (
                  <div className="rounded-sm m-0 text-successText bg-successBg w-fit px-2">Active</div>
                )}
              </div>
              <p className="text-mainText text-base font-medium mt-1">{integration.name}</p>
            </div>
          </div>
        </div>
        <hr className="my-2 border-1 border-neutral-4 dark:border-neutral-9" />{' '}
        <div className="flex flex-col md:flex-row gap-3 my-auto">
          {integration.actions?.some((x) => x.type === 'disconnect_url') ? (
            <div
              className="h-8 px-3 text-sm items-center rounded bg-Red-9 text-white cursor-pointer justify-center flex align-middle"
              onClick={() => setShowDisconnectModal(true)}
            >
              Disconnect {integration.category.toUpperCase()}
            </div>
          ) : (
            <>
              <Tooltip content={tooltip} side="bottom" className="w-fit" hidden={!tooltip}>
                <div
                  onClick={() => (disabled ? null : manageIntegration(integration))}
                  className={`h-8 px-3 font-medium ${integration.isActive ? 'text-paginationSelected' : 'bg-mainPurple hover:bg-tertiaryPurple active:active:bg-tertiaryPurple border-transparent text-white'} rounded border justify-center items-center gap-2 ${disabled ? 'disabled opacity-50 cursor-not-allowed' : 'cursor-pointer'} flex align-middle`}
                >
                  <div className={`text-sm leading-tight ${disabled ? 'disabled bg-mainPurple' : ''}`}>
                    {integration.isActive ? 'Manage' : 'Configure'}
                  </div>
                </div>
              </Tooltip>
              <div
                className="h-8 px-3 font-medium text-sm items-center rounded border border-neutral-5 text-paginationSelected cursor-pointer justify-center flex align-middle"
                onClick={() => window.open(integration.docsLink, '_blank')}
              >
                Read Doc
              </div>
            </>
          )}
        </div>
      </div>

      {showDisconnectModal && (
        <IntegrationRemoveModal
          name={integration.name}
          setShowModal={setShowDisconnectModal}
          handleRemoveIntegration={() => executeAction(integration?.actions?.find((x) => x.type === 'disconnect_url'))}
          category={integration.category}
        />
      )}
    </>
  )
}

export default IntegrationCard
